import React, { useState } from 'react';
import { Avatar, Popover } from 'antd';
import {
  CheckCircleFilled, CloseCircleFilled, ClockCircleFilled,
  MailOutlined, UserOutlined, PlusOutlined
} from '@ant-design/icons';
import { WfStepAssigneeTreeSelect } from './WorkflowStepAssigneePicker';
import dayjs from 'dayjs';
import { buildProgressItems } from './approvalProgressUtils';

function getInitial(name) {
  const first = (name || '?').split('、')[0].trim();
  return first.slice(0, 1) || '?';
}

function resolveTypeClass(type) {
  if (type === 'cc') return 'cc';
  if (type === 'executor') return 'executor';
  if (type === 'submit') return 'submit';
  if (type === 'add_sign_group') return 'approver';
  return 'approver';
}

/** 节点语义色调：normal 正常 / special 特殊流转 / terminal 终止 / current 进行中 / pending 未到 */
function resolveActionTone(item) {
  if (item.status === 'current' || item.action_label === '处理中') return 'current';

  const terminalTypes = new Set(['reject', 'revoke']);
  const specialTypes = new Set(['transfer', 'add_sign', 'return']);
  const specialLabels = new Set(['转交', '加签', '退回']);
  const terminalLabels = new Set(['驳回', '撤销']);

  if (terminalTypes.has(item.type) || terminalLabels.has(item.action_label)) return 'terminal';
  if (specialTypes.has(item.type) || specialLabels.has(item.action_label)) return 'special';
  if (item.status === 'done' || item.done !== false) return 'normal';
  return 'pending';
}

function resolveParticipantTone(participant) {
  if (participant.status === 'current' || participant.action_label === '处理中') return 'current';
  if (participant.done || participant.status === 'done') return 'normal';
  return 'pending';
}

function StatusIcon({ item }) {
  const tone = resolveActionTone(item);

  if (item.type === 'cc') {
    if (item.status === 'pending') {
      return <div className="progress-node-icon progress-node-icon--pending-dot" />;
    }
    return (
      <div className="progress-node-icon progress-node-icon--cc">
        <MailOutlined />
      </div>
    );
  }
  if (item.type === 'add_sign_group') {
    if (item.status === 'done' || item.done) {
      return <CheckCircleFilled className="progress-node-icon progress-node-icon--done" />;
    }
    if (item.status === 'current') {
      return <ClockCircleFilled className="progress-node-icon progress-node-icon--current" />;
    }
    return <div className="progress-node-icon progress-node-icon--pending-dot" />;
  }
  if (item.status === 'done' && item.done !== false) {
    if (tone === 'terminal') {
      return <CloseCircleFilled className="progress-node-icon progress-node-icon--terminal" />;
    }
    if (tone === 'special') {
      return <CheckCircleFilled className="progress-node-icon progress-node-icon--special" />;
    }
    return <CheckCircleFilled className="progress-node-icon progress-node-icon--done" />;
  }
  if (item.type === 'reject' || tone === 'terminal') {
    return <CloseCircleFilled className="progress-node-icon progress-node-icon--reject" />;
  }
  if (item.status === 'current') {
    return <ClockCircleFilled className="progress-node-icon progress-node-icon--current" />;
  }
  return <div className="progress-node-icon progress-node-icon--pending-dot" />;
}

function AddSignParticipantRow({ participant }) {
  const tone = resolveParticipantTone(participant);
  const actionClass = `progress-node-action--${tone}`;
  const showAction = participant.action_label;

  return (
    <div className="progress-addsign-participant-wrap">
      <div className="progress-addsign-participant">
        <Avatar size={32} shape="circle" className="progress-addsign-participant-avatar">
          {getInitial(participant.user_name)}
        </Avatar>
        <div className="progress-addsign-participant-info">
          <div className="progress-addsign-participant-title">
            <span className="progress-addsign-participant-name">{participant.user_name}</span>
            {participant.create_time && (
              <span className="progress-addsign-participant-time">
                {dayjs(participant.create_time).format('YYYY.MM.DD HH:mm')}
              </span>
            )}
          </div>
          <span className={`progress-addsign-participant-action ${actionClass}`}>
            <span className="progress-node-type-tag progress-node-type-tag--approver">
              {participant.role_label || '审批人'}
            </span>
            {showAction && (
              <span className="progress-node-action-text">（{participant.action_label}）</span>
            )}
          </span>
        </div>
      </div>
      {participant.comment && (
        <div className="progress-addsign-participant-comment">{participant.comment}</div>
      )}
    </div>
  );
}

function AddSignGroupNode({ item, isLast }) {
  const typeClass = resolveTypeClass(item.type);

  return (
    <div className={`progress-node ${isLast ? 'progress-node--last' : ''}`}>
      <div className="progress-node-left">
        <div className="progress-node-icon-wrap">
          <StatusIcon item={item} />
        </div>
        {!isLast && <div className="progress-node-line" />}
      </div>
      <div className="progress-node-body">
        <div className="progress-node-head progress-addsign-group-head">
          <Avatar size={40} shape="circle" className="progress-node-avatar progress-addsign-group-avatar">
            <UserOutlined />
          </Avatar>
          <div className="progress-node-info">
            <div className="progress-node-title">
              <span className="progress-node-name">{item.participant_count}位参与人</span>
              {item.create_time && (
                <span className="progress-node-time">
                  {dayjs(item.create_time).format('YYYY.MM.DD HH:mm')}
                </span>
              )}
            </div>
            <div className="progress-node-action progress-node-action--pending">
              <span className={`progress-node-type-tag progress-node-type-tag--${typeClass}`}>
                {item.type_label || '审批人'}
              </span>
            </div>
          </div>
        </div>
        <div className="progress-addsign-group-box">
          {(item.participants || []).map((participant) => (
            <AddSignParticipantRow
              key={`${participant.user_name}-${participant.task_id || 'placeholder'}`}
              participant={participant}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function AssigneePickNode({ item, isLast, assigneePick }) {
  const [open, setOpen] = useState(false);
  const stepId = item.step_id;
  const selected = assigneePick?.value?.[stepId] || [];
  const allowMultiple = item.allow_multiple !== false;
  const typeClass = resolveTypeClass(item.type);

  const handleChange = (nextValues) => {
    const ids = Array.isArray(nextValues) ? nextValues : (nextValues ? [nextValues] : []);
    assigneePick?.onChange?.({
      ...(assigneePick.value || {}),
      [stepId]: ids
    });
    if (!allowMultiple || ids.length >= 1) {
      setOpen(false);
    }
  };

  const pickerContent = (
    <div style={{ width: 320 }}>
      <WfStepAssigneeTreeSelect
        treeData={assigneePick?.treeData || []}
        loading={assigneePick?.loading}
        value={selected}
        onChange={handleChange}
        multiple={allowMultiple}
        disabled={assigneePick?.disabled}
        placeholder="选择职能组织下的人员"
      />
    </div>
  );

  return (
    <div className={`progress-node ${isLast ? 'progress-node--last' : ''}`}>
      <div className="progress-node-left">
        <div className="progress-node-icon-wrap">
          <div className="progress-node-icon progress-node-icon--pending-dot" />
        </div>
        {!isLast && <div className="progress-node-line" />}
      </div>
      <div className="progress-node-body">
        <Popover
          open={open}
          onOpenChange={setOpen}
          trigger="click"
          placement="bottomLeft"
          content={pickerContent}
        >
          <button type="button" className="progress-assignee-pick-trigger">
            <span className="progress-assignee-pick-add">
              <PlusOutlined />
            </span>
            <span className="progress-assignee-pick-text">
              <span className="progress-assignee-pick-title">{item.role_label || '审批人'}</span>
              <span className="progress-assignee-pick-hint">{item.pick_hint || '请添加审批人'}</span>
            </span>
          </button>
        </Popover>
        <div className={`progress-node-action progress-node-action--pending progress-assignee-pick-tag-row`}>
          <span className={`progress-node-type-tag progress-node-type-tag--${typeClass}`}>
            {item.type === 'cc' ? '抄送人' : '审批人'}
          </span>
          {item.node_label && (
            <span className="progress-node-step-label">{item.node_label}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ProgressNode({ item, isLast, assigneePick }) {
  if (item.is_assignee_pick) {
    return <AssigneePickNode item={item} isLast={isLast} assigneePick={assigneePick} />;
  }
  if (item.is_add_sign_group) {
    return <AddSignGroupNode item={item} isLast={isLast} />;
  }

  const tone = resolveActionTone(item);
  const typeClass = resolveTypeClass(item.type);
  const actionClass = `progress-node-action--${tone}`;

  const showActionText = item.show_action !== false && item.action_label;

  return (
    <div className={`progress-node ${isLast ? 'progress-node--last' : ''}`}>
      <div className="progress-node-left">
        <div className="progress-node-icon-wrap">
          <StatusIcon item={item} />
        </div>
        {!isLast && <div className="progress-node-line" />}
      </div>
      <div className="progress-node-body">
        <div className="progress-node-head">
          <Avatar size={40} shape="circle" className="progress-node-avatar">
            {getInitial(item.user_name)}
          </Avatar>
          <div className="progress-node-info">
            <div className="progress-node-title">
              <span className="progress-node-name">{item.user_name}</span>
              {item.create_time && (
                <span className="progress-node-time">
                  {dayjs(item.create_time).format('YYYY.MM.DD HH:mm')}
                </span>
              )}
            </div>
            <div className={`progress-node-action ${actionClass}`}>
              <span className={`progress-node-type-tag progress-node-type-tag--${typeClass}`}>
                {item.type_label || '审批人'}
              </span>
              {item.node_label && (
                <span className="progress-node-step-label">{item.node_label}</span>
              )}
              {item.multi_mode_label && (
                <span className="progress-node-multi-mode">{item.multi_mode_label}</span>
              )}
              {showActionText && (
                <span className="progress-node-action-text">（{item.action_label}）</span>
              )}
            </div>
          </div>
        </div>
        {item.comment && (
          <div className="progress-node-comment">{item.comment}</div>
        )}
      </div>
    </div>
  );
}

export default function ApprovalProgressTimeline({ process, assigneePick }) {
  const items = buildProgressItems(process, { assigneePick });
  const hasAssigneePick = items.some((item) => item.is_assignee_pick);

  if (!items.length) {
    if (process.workflow?.has_workflow === false) {
      return <div className="process-detail-empty">暂无配置的审批流程</div>;
    }
    return <div className="process-detail-empty">暂无审批进度</div>;
  }

  return (
    <div className="approval-progress">
      {hasAssigneePick && assigneePick?.enabled && (
        <div className="progress-assignee-pick-section-title">
          添加审批人
          <span className="progress-assignee-pick-required">*</span>
        </div>
      )}
      {items.map((item, idx) => (
        <ProgressNode
          key={item.key}
          item={item}
          isLast={idx === items.length - 1}
          assigneePick={assigneePick?.enabled ? assigneePick : null}
        />
      ))}
    </div>
  );
}
